import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getTaxDeliveryKba,
  updateTaxDeliveryKba,
  getReminderType,
  updateReminderType,
  ReminderType,
} from 'api/tax-delivery-settings';
import { AlertMessage } from 'utilities/utils';

export const TAX_DELIVERY_KBA_QUERY_KEY = ['tax-delivery-kba'];
export const TAX_DELIVERY_REMINDER_TYPE_QUERY_KEY = [
  'tax-delivery-reminder-type',
];

/**
 * Hook to fetch the KBA setting for tax delivery
 */
export function useTaxDeliveryKba() {
  return useQuery({
    queryKey: TAX_DELIVERY_KBA_QUERY_KEY,
    queryFn: getTaxDeliveryKba,
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60 * 2,
  });
}

/**
 * Hook to update the KBA setting for tax delivery
 */
export function useUpdateTaxDeliveryKba() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateTaxDeliveryKba,
    onSuccess: (data) => {
      queryClient.setQueryData(TAX_DELIVERY_KBA_QUERY_KEY, data);
    },
    onError: (error: any) => {
      AlertMessage(
        'error',
        error.message || 'Failed to update KBA setting',
        3000,
      );
    },
  });
}

/**
 * Hook to fetch the reminder type setting for tax delivery
 */
export function useReminderType() {
  return useQuery({
    queryKey: TAX_DELIVERY_REMINDER_TYPE_QUERY_KEY,
    queryFn: getReminderType,
    staleTime: 1000 * 60 * 60,
    cacheTime: 1000 * 60 * 60 * 2,
  });
}

/**
 * Hook to update the reminder type setting for tax delivery
 */
export function useUpdateReminderType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateReminderType,
    onSuccess: (data) => {
      queryClient.setQueryData(TAX_DELIVERY_REMINDER_TYPE_QUERY_KEY, data);
    },
    onError: (error: any) => {
      AlertMessage(
        'error',
        error.message || 'Failed to update reminder type setting',
        3000,
      );
    },
  });
}

/**
 * Convenience hook that returns both tax delivery settings
 */
export function useTaxDeliverySettings() {
  const kba = useTaxDeliveryKba();
  const reminderType = useReminderType();

  return {
    kba: kba.data ?? false,
    reminderType: (reminderType.data ?? 'remind_to_pay') as ReminderType,
    isLoading: kba.isLoading || reminderType.isLoading,
    isError: kba.isError || reminderType.isError,
  };
}
